import type { ChosenFile } from "@nexxt/api-client";
import type { auditEntryDto, CursorPage, MatchingPage, WithAnyOfText } from "@nexxt/contracts";
import type {
  AddInboundNoteInput,
  AttachInboundDocumentsInput,
  AuthorizeInboundInput,
  CreateInboundInput,
  creditNoteDto,
  DeleteInboundNoteInput,
  DownloadInboundDocumentInput,
  deletedInboundNoteDto,
  EditInboundNoteInput,
  GetInboundHistoryInput,
  GetInboundInput,
  GetPurchaseOrderHistoryInput,
  GetPurchaseOrderInput,
  GetPurchaseOrderRevisionInput,
  InboundActionInput,
  InboundReasonInput,
  inboundCandidatesDto,
  inboundDetailDto,
  inboundDto,
  inboundNoteDto,
  ListCreditNotesInput,
  ListInboundCandidatesInput,
  ListInboundNotesInput,
  ListInboundsInput,
  ListPoChangesInput,
  ListPurchaseOrderLinesInput,
  ListPurchaseOrderRevisionsInput,
  ListPurchaseOrdersInput,
  ProposePoChangesInput,
  PurchaseOrderActionInput,
  poChangeDto,
  purchaseOrderDto,
  purchaseOrderLineDto,
  purchaseOrderOptionsDto,
  purchaseOrderRevisionDto,
  purchaseOrderRevisionsDto,
  RemoveInboundDocumentInput,
  SendCreditNoteInput,
  SettleInboundShortageInput,
  savedPoChangesDto,
  TrackInboundInput,
  UpdateInboundInput,
  UpdateInboundPickupInput,
  WithdrawPoChangesInput,
} from "@nexxt/contracts/procurement";
import type * as z from "zod";

// What the purchase order and inbound screens ask of a gateway (ADR-169, ADR-171): one call per operation of
// procurement, with the input and the answer of its contract. Each app gives its own, on the client of its gateway,
// and the screens never see a route. The operations are those of the supplier and of the brand
// (ExternalProcurementService, ADR-168, ADR-170); what only Tomorrow calls the app writes on top of the screens.

export type PurchaseOrder = z.output<typeof purchaseOrderDto>;
export type PurchaseOrderOptions = z.output<typeof purchaseOrderOptionsDto>;
export type PurchaseOrderLine = z.output<typeof purchaseOrderLineDto>;
export type PoChange = z.output<typeof poChangeDto>;
export type SavedPoChanges = z.output<typeof savedPoChangesDto>;
export type PurchaseOrderRevisions = z.output<typeof purchaseOrderRevisionsDto>;
type PurchaseOrderRevision = z.output<typeof purchaseOrderRevisionDto>;
export type Inbound = z.output<typeof inboundDto>;
export type InboundDetail = z.output<typeof inboundDetailDto>;
export type InboundLine = InboundDetail["lines"][number];
export type InboundDocument = InboundDetail["documents"][number];
type InboundCandidates = z.output<typeof inboundCandidatesDto>;
export type InboundNote = z.output<typeof inboundNoteDto>;
type DeletedInboundNote = z.output<typeof deletedInboundNoteDto>;
export type CreditNote = z.output<typeof creditNoteDto>;
type AuditEntry = z.output<typeof auditEntryDto>;

/** The input of an operation as the screens give it: what the contract coerces (`page`, `limit`, …) as numbers. */
type Given<I> = { [K in keyof I]: unknown extends I[K] ? number | undefined : I[K] };
type Read<I, R> = (input: Given<I>, signal?: AbortSignal) => Promise<R>;
type Write<I, R> = (input: I) => Promise<R>;

/** The operations of the purchase order and inbound screens, by the names of procurement's RPC methods. */
export interface ProcurementClient {
  listPurchaseOrders: Read<WithAnyOfText<ListPurchaseOrdersInput>, MatchingPage<PurchaseOrder>>;
  getPurchaseOrderOptions: Read<Record<string, never>, PurchaseOrderOptions>;
  getPurchaseOrder: Read<GetPurchaseOrderInput, PurchaseOrder>;
  getPurchaseOrderHistory: Read<GetPurchaseOrderHistoryInput, CursorPage<AuditEntry>>;
  confirmPurchaseOrder: Write<PurchaseOrderActionInput, PurchaseOrder>;
  listPurchaseOrderLines: Read<ListPurchaseOrderLinesInput, CursorPage<PurchaseOrderLine>>;
  listPoChanges: Read<ListPoChangesInput, CursorPage<PoChange>>;
  proposePoChanges: Write<ProposePoChangesInput, SavedPoChanges>;
  withdrawPoChanges: Write<WithdrawPoChangesInput, SavedPoChanges>;
  listPurchaseOrderRevisions: Read<ListPurchaseOrderRevisionsInput, PurchaseOrderRevisions>;
  getPurchaseOrderRevision: Read<GetPurchaseOrderRevisionInput, PurchaseOrderRevision>;
  listInbounds: Read<WithAnyOfText<ListInboundsInput>, MatchingPage<Inbound>>;
  getInbound: Read<GetInboundInput, InboundDetail>;
  getInboundHistory: Read<GetInboundHistoryInput, CursorPage<AuditEntry>>;
  listInboundCandidates: Read<ListInboundCandidatesInput, InboundCandidates>;
  createInbound: Write<CreateInboundInput, InboundDetail>;
  updateInbound: Write<UpdateInboundInput, InboundDetail>;
  /** The tracking of an inbound on its way, all of it at once (B-51, ADR-212). */
  trackInbound: Write<TrackInboundInput, InboundDetail>;
  /** The ready date and the pickup contact of an inbound on its way, in every incoterm (B-89, ADR-244). */
  updateInboundPickup: Write<UpdateInboundPickupInput, InboundDetail>;
  submitInbound: Write<InboundActionInput, InboundDetail>;
  authorizeInbound: Write<AuthorizeInboundInput, InboundDetail>;
  refuseInbound: Write<InboundReasonInput, InboundDetail>;
  cancelInbound: Write<InboundReasonInput, InboundDetail>;
  attachInboundDocuments: Write<AttachInboundDocumentsInput, InboundDetail>;
  removeInboundDocument: Write<RemoveInboundDocumentInput, InboundDetail>;
  /** The bytes of a document, for its preview (ADR-132). */
  downloadInboundDocument: Read<DownloadInboundDocumentInput, Blob>;
  listInboundNotes: Read<ListInboundNotesInput, InboundNote[]>;
  addInboundNote: Write<AddInboundNoteInput, InboundNote>;
  editInboundNote: Write<EditInboundNoteInput, InboundNote>;
  deleteInboundNote: Write<DeleteInboundNoteInput, DeletedInboundNote>;
  /** Where the browser downloads a document, as an attachment (ADR-069): the same route as its bytes. */
  inboundDocumentUrl: (input: DownloadInboundDocumentInput) => string;
  /** The supplier's choice for the missing units of a received inbound (ADR-140): the inbound closes. */
  settleInboundShortage: Write<SettleInboundShortageInput, InboundDetail>;
  listCreditNotes: Read<WithAnyOfText<ListCreditNotesInput>, MatchingPage<CreditNote>>;
  /** The file of a credit note, uploaded first to the `inbound` folder (ADR-140). */
  sendCreditNote: Write<SendCreditNoteInput, CreditNote>;
  /** A file to the `inbound` folder of the files worker, before `attachInboundDocuments` names it (ADR-069). */
  uploadInboundFile: (file: ChosenFile) => Promise<void>;
}

let registered: ProcurementClient | undefined;

/** Once, when the app starts: before it, no purchase order screen loads (the route loaders read through it too). */
export function registerProcurementClient(client: ProcurementClient): void {
  registered = client;
}

export function procurementClient(): ProcurementClient {
  if (!registered) throw new Error("registerProcurementClient was not called when the app started");
  return registered;
}
